/* eslint-disable react-hooks/exhaustive-deps */
import { toast } from "react-toastify";
import { useMemo, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import anime from "animejs";
import { unwrapResult } from "@reduxjs/toolkit";
import { useDialog } from "app/hook";
import { RootState, AppDispatch } from "app/store";
import { DialogType, RestrictedFeatureSource } from "app/types";
import I18n from "common/i18n";
import Constants from "common/constant";
import { btnDelay, usePlaySound, shouldApplyGuestRestrictions } from "common/utils";
import { actions } from "features/creator/slice";
import { IconViewed } from "features/user/assets";
import { createProject } from "features/creator/api";
import { AppUser, AccountType } from "features/user/types";
import { CreateProjectModal, ProjectLimitDialog } from "features/creator/elements";
import { DisplayMode, ProjectType, ProjectEntity, ProjectPanelType } from "features/creator/types";
import {
  usePlan,
  usePlanLimits,
  PlanType,
  PLAN_LIMITS,
  useEnforcePlanLimit,
} from "features/subscription";
import {
  CardBg,
  IconDrawer,
  IconPublishCopiedOn,
  IconPublishCopiedOff,
  IconProjectCardFavorited,
  IconProjectCardFavoritedReverse,
} from "features/creator/assets";
import { projectAnimation } from "./util";

// マイアプリパネルの寸法定数
const MAX_SLOTS = 10; // 表示スロットの最大数
const CARD_W = 168; // カード／プレースホルダ／追加ボタン枠の幅
const DRAWER_W = 56; // ドロワータブの幅
const PADDING_X = 16; // スロットエリアの px-2（左右合計）
const CLOSED_VISIBLE = 242; // 閉時にパネルが占有・可視する幅

const ProjectCard = ({
  user,
  project,
  handleSelectProject,
}: {
  user: AppUser;
  project?: ProjectEntity;
  handleSelectProject: (projectId: string) => () => void;
}) => (
  <div
    id={`user-app-project-container-${project.project_id}`}
    onClick={handleSelectProject(project.project_id)}
    className="flex-col-el flex-center w-[168px] cursor-pointer pointer-events-auto"
  >
    <div className="flex-col-center">
      <div
        id={`user-app-project-publish-icon-${project.project_id}`}
        className="flex-col-center w-[48px] h-[40px]"
      >
        {project.published ? (
          <div className="flex-col-center w-[40px] h-[40px]">
            {project?.enable_copied ? <IconPublishCopiedOn /> : <IconPublishCopiedOff />}
          </div>
        ) : (
          <img
            src={`${Constants.assetHost}/assets/images/publish_on.png`}
            className="w-[36px] h-[36px]"
          />
        )}
      </div>

      <div className="flex-col-center rounded-[16px] p-2">
        <div className="flex-col-el flex-center w-[144px] h-[156px] absolute -z-0">
          <CardBg />
        </div>

        <div className="flex-col-center w-[120px] h-[148px] ml-3">
          <div className="flex-col-el flex-center absolute w-[62px] h-[64px]">
            <img
              className="w-full h-full"
              src={`${Constants.assetHost}/assets/images/project_card_switch.png`}
            />
          </div>

          <div className={`flex-col-center w-full h-full app-project-card-${project.project_id}`}>
            <img
              src={`${Constants.assetHost}/assets/images/${
                project.is_official
                  ? "app_card_official"
                  : project.author_uid === user.uid
                    ? "app_card_user"
                    : "app_card_shared"
              }.png`}
              className="w-full h-full"
            />

            <div className="flex-col-el flex-center absolute top-[32px] w-full px-[7px] h-[100px]">
              {/* 管理画面で処分されたアプリ。公開できないことがカードでも分かるように。
                  カードの絵は幅 88px しかないので、帯ではなく絵の上に重ねて 2 行で出す */}
              {project.banned && (
                <div className="flex-col-el flex-center absolute top-0 bottom-0 left-[7px] right-[7px] z-10 gap-1 bg-red-100/80">
                  <span className="material-symbols-outlined type-title text-white">block</span>
                  <p className="type-text type-sm text-white">
                    {I18n.t("MSG_CREATOR_APP_CARD_BLOCKED")}
                  </p>
                </div>
              )}
              {project?.cover || project?.capture ? (
                <div className="flex-col-center w-full h-full overflow-hidden bg-white relative">
                  <img
                    className="w-full h-full object-cover"
                    src={project?.cover ?? project?.capture}
                    onError={(e) =>
                      // @ts-expect-error EventTarget type lacks src property
                      (e.target.src = `${Constants.assetHost}/assets/images/default_capture.png`)
                    }
                  />
                  {/* ビュー数・いいね数オーバーレイ */}
                  <div className="flex-row-center absolute bottom-0 left-0 right-0 gap-2 bg-white/80 py-[2px]">
                    <div className="flex-row-center">
                      <div
                        id={`app-project-card-viewed-${project.project_id}`}
                        className="flex-col-center w-[24px] h-[24px]"
                      >
                        <IconViewed />
                      </div>
                      <p className="text-body text-[16px] leading-6 text-brown-100">
                        {project.viewed}
                      </p>
                    </div>
                    <div className="flex-row-center">
                      <div
                        id={`app-project-card-favorited-${project.project_id}`}
                        className="flex-col-center w-[24px] h-[24px]"
                      >
                        {project.is_favorited ? (
                          <IconProjectCardFavorited />
                        ) : (
                          <IconProjectCardFavoritedReverse />
                        )}
                      </div>
                      <p className="text-body text-[16px] leading-6 text-red-100">
                        {project.favorited}
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex-col-el justify-end w-full h-full bg-white">
                  {/* ビュー数・いいね数オーバーレイ（空の場合） */}
                  <div className="flex-row-center gap-2 bg-white/80 py-[2px]">
                    <div className="flex-row-center">
                      <div
                        id={`app-project-card-viewed-${project.project_id}`}
                        className="flex-col-center w-[24px] h-[24px]"
                      >
                        <IconViewed />
                      </div>
                      <p className="text-body text-[16px] leading-6 text-brown-100">
                        {project.viewed}
                      </p>
                    </div>
                    <div className="flex-row-center">
                      <div
                        id={`app-project-card-favorited-${project.project_id}`}
                        className="flex-col-center w-[24px] h-[24px]"
                      >
                        {project.is_favorited ? (
                          <IconProjectCardFavorited />
                        ) : (
                          <IconProjectCardFavoritedReverse />
                        )}
                      </div>
                      <p className="text-body text-[16px] leading-6 text-red-100">
                        {project.favorited}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>

    <div className="flex-row-center w-full">
      <p className="text-body text-center text-body-ellipsis text-[18px] leading-6 text-textcolor-black w-full">
        {project.name}
      </p>
    </div>
  </div>
);

const NewAppButton = ({ onPointerDown }: { onPointerDown: () => void }) => (
  <div className="flex-col-el flex-center w-[168px]">
    <div className="flex-col-center w-[48px] h-[40px]" />

    <div className="flex-col-center rounded-2xl p-2">
      <div className="flex-col-el flex-center w-[144px] h-[156px] relative">
        {/* 差込口 */}
        <div className="flex-col-el flex-center absolute w-full h-full">
          <CardBg className="w-full h-full" />
        </div>

        {/* 差込口の中に既存の緑ボタンを縮小して配置（左の切り欠きを避け矩形部分の中心へ右寄せ） */}
        <div className="flex-col-center upsilon cursor-pointer pointer-events-auto ml-4">
          <div
            id="user-project-panel-new-app-btn"
            onPointerDown={onPointerDown}
            className="flex-col-el flex-center !flex gap-2 btn btn-primary w-[104px] h-[116px] !p-2 !m-0"
          >
            <div className="flex-row-center">
              <div className="flex-col-el flex-center w-[32px] h-[8px] bg-white absolute" />
              <div className="flex-col-center w-[8px] h-[32px] bg-white" />
            </div>

            <div className="flex-col-el flex-center">
              <p className="text-body text-white text-center type-sm leading-5 whitespace-pre-line">
                {I18n.t("MSG_CREATOR_CREATE_APP")}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div className="flex-row-center w-full h-[24px]" />
  </div>
);

export const UserProjectPanel = ({
  user,
  projects,
}: {
  user: AppUser;
  projects: ProjectEntity[];
}) => {
  const play = usePlaySound();
  const handleDialog = useDialog();
  const enforcePlanLimit = useEnforcePlanLimit();
  const dispatch = useDispatch<AppDispatch>();
  const plan = usePlan();
  const limits = usePlanLimits();
  const [create, setCreate] = useState(false);
  const [guidance, setGuidance] = useState(false);
  const projectPanel = useSelector((state: RootState) => state.creator.projectPanel);
  const isCopyProject = useSelector((state: RootState) => state.creator.isCopyProject);
  const selectedProjectId = useSelector((state: RootState) => state.creator.selectedProjectId);
  const sortedProjects = useMemo(
    () => [...projects].sort((a, b) => (dayjs(a.updated_time) < dayjs(b.updated_time) ? 1 : -1)),
    [projects],
  );

  const [localSelectedProjectId, setLocalSelectedProjectId] = useState(selectedProjectId);
  const active = useSelector((state: RootState) => state.user.active);

  // プランに応じた表示スロット数（常に制限数+1、最大制限プランのときのみ10）
  const slotCount = useMemo(() => {
    const isOldTrialUser =
      plan === PlanType.TRIAL &&
      active.account_type === AccountType.GUEST &&
      !shouldApplyGuestRestrictions(active.created_at);
    const projectLimit = isOldTrialUser
      ? PLAN_LIMITS[PlanType.FREE].projects.limit
      : limits.projects.limit;
    return limits.projects.unlimited ? MAX_SLOTS : Math.min(projectLimit + 1, MAX_SLOTS);
  }, [plan, active.account_type, active.created_at, limits]);
  // 実表示数 = アプリ数 + 追加ボタン1個（上限 slotCount まで。満杯時は追加ボタンなし）
  const displayedSlots = Math.min(sortedProjects.length + 1, slotCount);
  // 2行固定なので列数 = ceil(表示数 / 2)。列数からグリッド幅・パネル幅を導出
  const cols = Math.ceil(displayedSlots / 2);
  const gridWidth = cols * CARD_W;
  const layoutWidth = Math.max(DRAWER_W + PADDING_X + gridWidth, CLOSED_VISIBLE);

  useEffect(() => {
    setLocalSelectedProjectId(selectedProjectId);
  }, [selectedProjectId]);

  useEffect(() => {
    if (isCopyProject) {
      projectAnimation(localSelectedProjectId, sortedProjects[0].project_id, () => {
        dispatch(actions.handleUpdateIsCopyProject(false));
        setLocalSelectedProjectId(sortedProjects[0].project_id);
        dispatch(actions.updateSelectedProjectId(sortedProjects[0].project_id));
      });
    }
  }, [isCopyProject]);

  const handleExpend = () => {
    play();
    if (projectPanel === ProjectPanelType.USER) {
      dispatch(actions.updateProjectPanel(ProjectPanelType.NONE));
    } else {
      dispatch(actions.updateSortFilterPanel(true));
      dispatch(actions.updateProjectPanel(ProjectPanelType.USER));
    }
  };

  const handleSelectProject = (projectId: string) => () => {
    if (projectId !== localSelectedProjectId) {
      play();
      setLocalSelectedProjectId(projectId);
      projectAnimation(localSelectedProjectId, projectId, () => {
        dispatch(actions.updateProjectPanel(ProjectPanelType.NONE));
        dispatch(actions.updateDisplayMode(DisplayMode.CAPTURE));
        dispatch(actions.updateSelectedProjectId(projectId));
      });
    }
  };

  const handleOnTransitionEnd = () => {
    if (projectPanel === ProjectPanelType.NONE && selectedProjectId !== localSelectedProjectId) {
      dispatch(actions.updateDisplayMode(DisplayMode.CAPTURE));
      dispatch(actions.updateSelectedProjectId(localSelectedProjectId));
    }
  };

  const handleCreateProjectConfirm = () => {
    play();
    btnDelay(() => {
      // OLD TRIALユーザー判定（2025-11-02以前に作成されたGUESTユーザー）
      const isOldTrialUser =
        plan === PlanType.TRIAL &&
        active.account_type === AccountType.GUEST &&
        !shouldApplyGuestRestrictions(active.created_at);

      // 制限値を計算（OLD TRIALはFREEと同じ制限）
      const projectLimit = isOldTrialUser
        ? PLAN_LIMITS[PlanType.FREE].projects.limit
        : limits.projects.limit;

      // 無制限プランはチェックスキップ
      if (limits.projects.unlimited) {
        setCreate(true);
        return;
      }

      // 制限に達している場合
      if (sortedProjects.length >= projectLimit) {
        // TRIAL は新 Dialog、FREE は RestrictedFeatureDialog
        if (plan === PlanType.TRIAL || plan === PlanType.FREE) {
          enforcePlanLimit(RestrictedFeatureSource.PROJECT_LIMIT);
          return;
        }
        // EDUCATION/PREMIUM/BUILDER: ProjectLimitDialog表示（通知のみ）
        setGuidance(true);
        return;
      }

      // 制限内であればプロジェクト作成モーダルを表示
      setCreate(true);
    });
  };

  // プラン別ダイアログのアクションハンドラ（EDUCATION/STANDARD用）
  // TRIAL/FREEは直接リダイレクトするのでこの関数は呼ばれない
  const _handleGuidanceAction = () => {
    play();
    setGuidance(false);
  };
  const handleCreateProject = (name: string, type: ProjectType) => {
    play();
    handleDialog({ type: DialogType.LOADING, value: true });
    btnDelay(() => {
      dispatch(
        createProject({
          uid: user.uid,
          name,
          type,
          tags: "オリジナル",
        }),
      )
        .then(unwrapResult)
        .then((result) => {
          anime({
            targets: document.getElementById(
              `user-app-project-container-${result.project.project_id}`,
            ),
            scale: [1.3, 1],
            opacity: [0, 1],
            duration: 400,
            easing: "easeInOutQuad",
          });
        })
        .catch((error) => {
          // 以前は失敗しても何も出ず、押しても作られないだけだった
          toast.warn(
            <div>
              <p className="type-text type-sm text-textcolor-black">
                {I18n.t(
                  error?.code === "QUOTA_EXCEEDED"
                    ? "MSG_TOAST_PROJECT_QUOTA_EXCEEDED"
                    : "MSG_TOAST_FAILED",
                )}
              </p>
            </div>,
          );
        })
        .finally(() => {
          setCreate(false);
          setTimeout(() => handleDialog({ type: DialogType.LOADING, value: false }), 300);
        });
    });
  };
  const handleCreateProjectCancel = () => {
    play();
    btnDelay(() => setCreate(false));
  };

  const handleCloseGuidance = () => {
    play();
    btnDelay(() => setGuidance(false));
  };

  return (
    <>
      <CreateProjectModal
        visible={create}
        name={`${I18n.t("MSG_CREATOR_CREATE_APP_NAME_PREFIX")}${projects.length + 1}`}
        type={ProjectType.BASIC}
        create={handleCreateProject}
        close={handleCreateProjectCancel}
      />

      <ProjectLimitDialog open={guidance} type="app" close={handleCloseGuidance} />

      <div
        onTransitionEnd={handleOnTransitionEnd}
        style={{
          transform: `translateX(${
            projectPanel === ProjectPanelType.USER ? -layoutWidth + CLOSED_VISIBLE : 0
          }px)`,
        }}
        className="flex-row-el transition-transform duration-700 will-change-transform origin-top-left pointer-events-auto"
      >
        <div className="flex-col-el relative h-[468px] shrink-0" style={{ width: layoutWidth }}>
          <div className="flex-col-el w-full items-end">
            <div className="flex-row-el w-full">
              <div>
                <div className="flex-col-el absolute top-0 w-[56px] h-[254px] bg-[#E1AD60] rounded-tl-[24px] rounded-bl-[32px]" />

                <div
                  id="user-project-panel-drawer"
                  onClick={handleExpend}
                  className="flex-col-el shrink-0 items-center justify-between relative w-[56px] h-[246px] rounded-l-[24px] py-4 cursor-pointer bg-orange-100 overflow-hidden"
                >
                  <p className="text-body text-vertical whitespace-nowrap text-white text-[24px] leading-6">
                    {I18n.t("MSG_CREATOR_APP_MY_APPS")}
                  </p>

                  <div>
                    <IconDrawer />
                  </div>
                </div>
              </div>

              <div className="flex-col-el w-full">
                <div className="flex-col-el absolute top-0 -ml-[1px] w-full h-[477px] bg-[#E1AD60] rounded-bl-[20px]" />

                <div className="flex-row-el items-center justify-start relative w-full h-[468px] transition-transform duration-700 px-2 rounded-bl-[16px] bg-orange-10 overflow-hidden border-solid border-0 border-t-[1px] border-[#E5B876]">
                  <div style={{ width: gridWidth }} className="grid grid-flow-col grid-rows-2">
                    {Array(displayedSlots)
                      .fill(0)
                      .map((_, index) => {
                        if (index < sortedProjects.length) {
                          return (
                            <ProjectCard
                              key={sortedProjects[index].project_id}
                              user={user}
                              project={sortedProjects[index]}
                              handleSelectProject={handleSelectProject}
                            />
                          );
                        }
                        // 最後のアプリの直後のスロットに「あたらしく追加」ボタン
                        return (
                          <NewAppButton key="new-app" onPointerDown={handleCreateProjectConfirm} />
                        );
                      })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
